import { Tooltip as TooltipPrimitive } from "radix-ui";
import type { ReactElement, ReactNode } from "react";
import { Kbd } from "~/foundation/components/Kbd";
import { depths } from "~/foundation/styles/depths";

export interface TooltipProps {
  content: ReactNode;
  /** Declarative shortcut appended as a <Kbd> chip, e.g. ["Meta", "."]. */
  shortcut?: readonly string[];
  side?: "top" | "bottom" | "left" | "right";
  /** Logical placement — resolved to a physical side by the document direction. */
  placement?: "block-start" | "block-end" | "inline-start" | "inline-end";
  delayDuration?: number;
  children: ReactElement;
}

function resolveSide(props: Pick<TooltipProps, "side" | "placement">): TooltipProps["side"] {
  if (props.side) {
    return props.side;
  }
  const rtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";
  switch (props.placement) {
    case "block-start":
      return "top";
    case "block-end":
      return "bottom";
    case "inline-start":
      return rtl ? "right" : "left";
    case "inline-end":
      return rtl ? "left" : "right";
    default:
      return "bottom";
  }
}

/** Mount once, high in the tree. 500ms is the spec delay for the whole app. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={500} skipDelayDuration={300}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export function Tooltip({ content, shortcut, delayDuration, children, ...rest }: TooltipProps) {
  return (
    <TooltipPrimitive.Root {...(delayDuration === undefined ? {} : { delayDuration })}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={resolveSide(rest)}
          sideOffset={6}
          className="slate-pop flex items-center gap-2 rounded-sm bg-tooltip-bg px-2 py-1 text-small text-tooltip-text shadow-menu"
          style={{ zIndex: depths.tooltip }}
        >
          {content}
          {shortcut ? <Kbd keys={shortcut} tone="inverted" /> : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
